import * as React from "react"
import { cn } from "@/lib/utils"

export interface ButtonProps
    extends React.ButtonHTMLAttributes<HTMLButtonElement> {
    variant?: "default" | "outline" | "ghost" | "link" | "destructive" | "secondary" | "premium" | "glass"
    size?: "default" | "sm" | "lg" | "icon" | "xl"
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
    ({ className, variant = "default", size = "default", ...props }, ref) => {

        const variants = {
            default: "bg-blue-600 text-white hover:bg-blue-500 shadow-lg shadow-blue-500/20 border-b-2 border-blue-800 active:border-b-0 active:translate-y-[2px]",
            destructive: "bg-red-500 text-white hover:bg-red-600 shadow-sm",
            outline: "border-2 border-slate-800 bg-transparent text-slate-300 hover:bg-slate-800 hover:text-white hover:border-slate-700",
            secondary: "bg-slate-800 text-white hover:bg-slate-700 shadow-sm",
            ghost: "text-slate-400 hover:bg-slate-800 hover:text-white",
            link: "text-blue-500 underline-offset-4 hover:underline",
            premium: "bg-gradient-to-r from-blue-600 via-indigo-600 to-purple-600 text-white hover:opacity-90 shadow-xl shadow-indigo-500/20 border-b-4 border-indigo-900 active:border-b-0 active:translate-y-[4px]",
            glass: "bg-white/5 backdrop-blur-md border border-white/10 text-white hover:bg-white/10"
        }

        const sizes = {
            default: "h-10 px-6 py-2 rounded-xl text-sm font-bold tracking-tight",
            sm: "h-8 rounded-lg px-4 text-[10px] font-black uppercase tracking-widest",
            lg: "h-14 rounded-2xl px-10 text-lg font-black tracking-tight",
            xl: "h-20 rounded-3xl px-12 text-xl font-black tracking-tighter",
            icon: "h-10 w-10 rounded-xl",
        }

        return (
            <button
                className={cn(
                    "inline-flex items-center justify-center whitespace-nowrap transition-all focus-visible:outline-none disabled:pointer-events-none disabled:opacity-50 uppercase tracking-widest",
                    variants[variant],
                    sizes[size],
                    className
                )}
                ref={ref}
                {...props}
            />
        )
    }
)
Button.displayName = "Button"

export { Button }
